import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemAvatar,
    Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, Tab, TextField, FormControl, InputLabel, Select, MenuItem,
    Card, CardContent, Divider, Chip, ListItemIcon, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import ChatIcon from '@mui/icons-material/Chat';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import CancelIcon from '@mui/icons-material/Cancel';
import FlagIcon from '@mui/icons-material/Flag';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';

import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function ITSupplierDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [supplier, setSupplier] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    
    // Conversation
    const [conversations, setConversations] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    
    // Files
    const [files, setFiles] = useState([]);
    
    // Contracts
    const [contracts, setContracts] = useState([]);
    const [openContract, setOpenContract] = useState(false);
    const [newContract, setNewContract] = useState({ name: '', start_date: '', end_date: '', status: 'Active', file: null });

    // Contacts
    const [contacts, setContacts] = useState([]);
    const [openContact, setOpenContact] = useState(false);
    const [newContact, setNewContact] = useState({ name: '', position: '', email: '', phone: '' });

    // Edit Mode
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchSupplier();
        fetchCountries();
        fetchConversations();
        fetchFiles();
        fetchContracts();
        fetchContacts();
    }, [id]);

    const fetchContacts = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/contacts`, { headers });
            setContacts(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchSupplier = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}`, { headers });
            setSupplier(res.data);
            setEditData(res.data);
            setLoading(false);
        } catch (e) { console.error(e); setLoading(false); }
    };

    const fetchConversations = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/conversations`, { headers });
            setConversations(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchFiles = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/files`, { headers });
            setFiles(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchContracts = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/contracts`, { headers });
            setContracts(res.data);
        } catch (e) {console.error(e);}
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API_URL}/it-suppliers/${id}`, editData, { headers });
            await fetchSupplier();
            setEditMode(false);
        } catch (e) {console.error(e);}
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/conversations`, { message: newMessage }, { headers });
            setNewMessage('');
            fetchConversations();
        } catch (e) {console.error(e);}
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/files`, formData, { headers });
            fetchFiles();
        } catch (e) {
            console.error(e);
            alert("File upload failed: " + (e.response?.data?.error || e.message));
        }
    };

    const handleDeleteFile = async (fileId) => {
        if(!window.confirm("Delete this file?")) return;
        try {
            await axios.delete(`${API_URL}/it-suppliers/${id}/files/${fileId}`, { headers });
            fetchFiles();
        } catch (e) {console.error(e);}
    };

    const handleAddContract = async () => {
        const formData = new FormData();
        formData.append('name', newContract.name);
        formData.append('start_date', newContract.start_date);
        formData.append('end_date', newContract.end_date);
        formData.append('status', newContract.status);
        if (newContract.file) formData.append('file', newContract.file);

        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/contracts`, formData, { headers });
            setOpenContract(false);
            setNewContract({ name: '', start_date: '', end_date: '', status: 'Active', file: null });
            fetchContracts();
        } catch (e) {console.error(e);}
    };

    const handleAddContact = async () => {
        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/contacts`, newContact, { headers });
            setOpenContact(false);
            setNewContact({ name: '', position: '', email: '', phone: '' });
            fetchContacts();
        } catch (e) {console.error(e);}
    };

    const handleDeleteContact = async (contact_id) => {
         if(!window.confirm("Delete this contact?")) return;
         try {
             await axios.delete(`${API_URL}/it-suppliers/${id}/contacts/${contact_id}`, { headers });
             fetchContacts();
         } catch (e) {console.error(e);}
    };

    if (loading) return <Loading />;
    if (!supplier) return <Typography>Supplier not found</Typography>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header Section */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', color: 'white' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: '100%' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/it-suppliers')}
                            sx={{ color: 'white', mb: 2, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Back to IT Suppliers
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="h3" fontWeight="bold">{supplier.name}</Typography>
                            <Chip label="Active" sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                <BusinessIcon fontSize="small" />
                                <Typography>IT Supplier</Typography>
                            </Box>
                            {supplier.country_name && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                    <FlagIcon fontSize="small" />
                                    <Typography>{supplier.country_name}</Typography>
                                </Box>
                            )}
                            {supplier.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                    <LocationOnIcon fontSize="small" />
                                    <Typography>{supplier.location}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!editMode ? (
                            <Button
                                onClick={() => setEditMode(true)}
                                variant="contained"
                                size="medium"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    height: 48,
                                    minWidth: 48,
                                    px: 0,
                                    borderRadius: 3
                                }}
                            >
                                <EditIcon />
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUpdate}
                                    variant="contained"
                                    size="medium"
                                    sx={{
                                        bgcolor: '#4caf50',
                                        color: 'white',
                                        height: 48,
                                        minWidth: 48,
                                        px: 0,
                                        borderRadius: 3,
                                        '&:hover': { bgcolor: '#388e3c' }
                                    }}
                                >
                                    <SaveIcon />
                                </Button>
                                <Button
                                    onClick={() => { setEditMode(false); setEditData(supplier); }}
                                    variant="contained"
                                    size="medium"
                                    sx={{
                                        bgcolor: '#f44336',
                                        color: 'white',
                                        height: 48,
                                        minWidth: 48,
                                        px: 0,
                                        borderRadius: 3,
                                        marginLeft: 1,
                                        '&:hover': { bgcolor: '#d32f2f' }
                                    }}
                                >
                                    <CancelIcon />
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper sx={{ width: '100%', mb: 2, borderRadius: 3, overflow: 'hidden' }}>
                        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                            <Tab icon={<BusinessIcon />} iconPosition="start" label="Overview" />
                            <Tab icon={<PeopleIcon />} iconPosition="start" label="Contacts" />
                            <Tab icon={<ChatIcon />} iconPosition="start" label="Conversations" />
                            <Tab icon={<FolderIcon />} iconPosition="start" label="Files" />
                            <Tab icon={<GavelIcon />} iconPosition="start" label="Contracts" />
                        </Tabs>

                        <TabPanel value={tab} index={0}>
                             <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DescriptionIcon color="primary" />
                                    <Typography variant="h6" fontWeight="bold">Comments / Notes</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                {editMode ? (
                                    <TextField fullWidth multiline rows={4} value={editData.comment || ''} onChange={(e) => setEditData({...editData, comment: e.target.value})} />
                                ) : (
                                    <Typography paragraph color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, bgcolor: '#F8FAFC', p: 2, borderRadius: 2 }}>{supplier.comment || 'No comments provided.'}</Typography>
                                )}
                            </Box>

                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                 <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <BusinessIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Name</Typography>
                                        </Box>
                                        {editMode ? (
                                            <TextField fullWidth size="small" value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} />
                                        ) : (
                                            <Typography variant="body2">{supplier.name}</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                 <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <FlagIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Country</Typography>
                                        </Box>
                                        {editMode ? (
                                            <Select fullWidth size="small" value={editData.country_id || ''} onChange={(e) => setEditData({ ...editData, country_id: e.target.value })}>
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {countries.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Typography variant="body2">{supplier.country_name || 'N/A'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <LocationOnIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Location</Typography>
                                        </Box>
                                        {editMode ? (
                                            <TextField fullWidth size="small" value={editData.location || ''} onChange={(e) => setEditData({...editData, location: e.target.value})} />
                                        ) : (
                                            <Typography variant="body2">{supplier.location || 'N/A'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <LanguageIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Website</Typography>
                                        </Box>
                                        {editMode ? (
                                            <TextField fullWidth size="small" value={editData.website || ''} onChange={(e) => setEditData({...editData, website: e.target.value})} />
                                        ) : (
                                            <Typography variant="body2">
                                                {supplier.website ? (
                                                    <a href={supplier.website} target="_blank" rel="noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{supplier.website}</a>
                                                ) : 'N/A'}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PhoneIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Phone</Typography>
                                        </Box>
                                        {editMode ? (
                                            <TextField fullWidth size="small" value={editData.phone || ''} onChange={(e) => setEditData({...editData, phone: e.target.value})} />
                                        ) : (
                                            <Typography variant="body2">{supplier.phone || 'N/A'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={1}>
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold">Contacts</Typography>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenContact(true)} sx={{ borderRadius: 2 }}>
                                    Add Contact
                                </Button>
                            </Box>
                            <Grid container spacing={2}>
                                {contacts.map(c => (
                                    <Grid item xs={12} sm={6} md={4} key={c.id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 3,
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: 'primary.main' }
                                            }}
                                        >
                                            <CardContent>
                                                <Box display="flex" alignItems="center" mb={2}>
                                                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                                                        <PersonIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                                        <Typography variant="body2" color="textSecondary">{c.position}</Typography>
                                                    </Box>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <EmailIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                    <Typography variant="caption">{c.email || 'N/A'}</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center">
                                                    <PhoneIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                                    <Typography variant="caption">{c.phone || 'N/A'}</Typography>
                                                </Box>
                                                <Box mt={2} display="flex" justifyContent="flex-end">
                                                     <IconButton color="error" size="small" onClick={() => handleDeleteContact(c.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {contacts.length === 0 && (
                                     <Grid item xs={12}>
                                        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                                            <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="h6" color="textSecondary" gutterBottom>No contacts found</Typography>
                                            <Button variant="outlined" onClick={() => setOpenContact(true)}>Add Contact</Button>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={2}>
                             {/* Conversations matching ITProjectDetails chat somewhat, but keeping original logic */}
                             <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ height: 500, display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                                        <Box flex={1} overflow="auto" p={2}>
                                            <List>
                                                {conversations.length === 0 && (
                                                    <Typography color="textSecondary" align="center" mt={4}>No conversations yet.</Typography>
                                                )}
                                                {conversations.map(c => (
                                                    <ListItem key={c.id} alignItems="flex-start">
                                                        <ListItemAvatar>
                                                            <Avatar sx={{ bgcolor: 'primary.main' }}>{c.user ? c.user[0] : 'U'}</Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText 
                                                            primary={
                                                                <Box display="flex" justifyContent="space-between">
                                                                    <Typography variant="subtitle2" fontWeight="bold">{c.user}</Typography>
                                                                    <Typography variant="caption" color="textSecondary">{new Date(c.timestamp).toLocaleString()}</Typography>
                                                                </Box>
                                                            } 
                                                            secondary={
                                                                <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                                                                    {c.message}
                                                                </Typography>
                                                            } 
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                        <Divider />
                                        <Box p={2} bgcolor="#F9FAFB">
                                            <Grid container spacing={2}>
                                                <Grid item xs>
                                                    <TextField 
                                                        fullWidth 
                                                        size="small" 
                                                        placeholder="Type a message..." 
                                                        value={newMessage} 
                                                        onChange={(e) => setNewMessage(e.target.value)} 
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                                        InputProps={{ sx: { borderRadius: 2 } }}
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Button variant="contained" endIcon={<SendIcon />} onClick={handleSendMessage} sx={{ borderRadius: 2 }}>
                                                        Send
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </TabPanel>
                        
                        <TabPanel value={tab} index={3}>
                             <Box mb={2} display="flex" justifyContent="flex-end">
                                <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2 }}>
                                    Upload File
                                    <input type="file" hidden onChange={handleFileUpload} />
                                </Button>
                            </Box>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                            <TableCell>Filename</TableCell>
                                            <TableCell>Uploaded By</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {files.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">No files uploaded.</TableCell>
                                            </TableRow>
                                        )}
                                        {files.map(f => (
                                            <TableRow key={f.id}>
                                                <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <DescriptionIcon color="action" sx={{ mr: 1 }} />
                                                    {f.filename}
                                                </TableCell>
                                                <TableCell>{f.uploaded_by}</TableCell>
                                                <TableCell>{new Date(f.uploaded_at).toLocaleString()}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton href={`${API_URL}/it-suppliers/${id}/files/${f.id}/download`} target="_blank" size="small">
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton color="error" onClick={() => handleDeleteFile(f.id)} size="small">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        <TabPanel value={tab} index={4}>
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold">Contracts</Typography>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenContract(true)} sx={{ borderRadius: 2 }}>
                                    Add Contract
                                </Button>
                            </Box>
                            <Grid container spacing={3}>
                                {contracts.map(c => (
                                    <Grid item xs={12} md={6} key={c.id}>
                                        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                                            <CardContent>
                                                <Box display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="h6">{c.name}</Typography>
                                                    <Chip 
                                                        label={c.status} 
                                                        color={c.status === 'Active' ? 'success' : c.status === 'Expired' ? 'error' : 'warning'} 
                                                        size="small" 
                                                    />
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                    {c.start_date ? new Date(c.start_date).toLocaleDateString() : 'N/A'} - {c.end_date ? new Date(c.end_date).toLocaleDateString() : 'N/A'}
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                 {c.has_file ? (
                                                     <Button 
                                                        variant="outlined" 
                                                        size="small" 
                                                        startIcon={<DownloadIcon />} 
                                                        href={`${API_URL}/it-suppliers/${id}/contracts/${c.id}/download`} 
                                                        target="_blank"
                                                        fullWidth
                                                    >
                                                        Download Agreement
                                                    </Button>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">No document attached</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                 {contracts.length === 0 && (
                                    <Grid item xs={12}>
                                         <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                                            <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="h6" color="textSecondary" gutterBottom>No contracts found</Typography>
                                            <Button variant="outlined" onClick={() => setOpenContract(true)}>Add Contract</Button>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </TabPanel>
                    </Paper>
                </Grid>
            </Grid>

            {/* Dialogs */}
            <Dialog open={openContact} onClose={() => setOpenContact(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add Contact Person</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField 
                            label="Name" 
                            fullWidth 
                            value={newContact.name} 
                            onChange={(e) => setNewContact({...newContact, name: e.target.value})} 
                        />
                        <TextField 
                            label="Position" 
                            fullWidth 
                            value={newContact.position} 
                            onChange={(e) => setNewContact({...newContact, position: e.target.value})} 
                        />
                        <TextField 
                            label="Email" 
                            fullWidth 
                            value={newContact.email} 
                            onChange={(e) => setNewContact({...newContact, email: e.target.value})} 
                        />
                        <TextField 
                            label="Phone" 
                            fullWidth 
                            value={newContact.phone} 
                            onChange={(e) => setNewContact({...newContact, phone: e.target.value})} 
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenContact(false)}>Cancel</Button>
                    <Button onClick={handleAddContact} variant="contained" disabled={!newContact.name}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openContract} onClose={() => setOpenContract(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Contract</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField 
                            label="Contract Name" 
                            fullWidth 
                            value={newContract.name} 
                            onChange={(e) => setNewContract({...newContract, name: e.target.value})} 
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField 
                                    label="Start Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth 
                                    value={newContract.start_date} 
                                    onChange={(e) => setNewContract({...newContract, start_date: e.target.value})} 
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    label="End Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth 
                                    value={newContract.end_date} 
                                    onChange={(e) => setNewContract({...newContract, end_date: e.target.value})} 
                                />
                            </Grid>
                        </Grid>
                        <FormControl fullWidth>
                            <TextField 
                                select 
                                label="Status" 
                                value={newContract.status} 
                                onChange={(e) => setNewContract({...newContract, status: e.target.value})}
                                SelectProps={{ native: true }}
                            >
                                <option value="Active">Active</option>
                                <option value="Expired">Expired</option>
                                <option value="Pending">Pending</option>
                            </TextField>
                        </FormControl>
                        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                            Upload Contract Document
                            <input type="file" hidden onChange={(e) => setNewContract({...newContract, file: e.target.files[0]})} />
                        </Button>
                        {newContract.file && <Typography variant="caption">{newContract.file.name}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenContract(false)}>Cancel</Button>
                    <Button onClick={handleAddContract} variant="contained" disabled={!newContract.name}>Save Contract</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ITSupplierDetails;
